<template>
    <main v-if="data" class="overflow-hidden relative min-h-screen pt-12">
        <div
            class="bg-vann absolute top-1/3 h-[300vh] left-[-75%] right-[-75%] rounded-t-[50%]"
        ></div>
        <div
            class="relative flex flex-col md:grid gap-8 md:grid-cols-2 max-w-4xl mx-4 md:mx-auto pb-6 md:py-20 text-fjell shadow-lg md:shadow-none"
        >
            <ContentRenderer :value="data">
                <div class="col-span-2 shadow-2xl">
                    <div class="bg-white p-6 md:p-16 rounded-t-xl">
                        <h1 class="font-heading1 mb-4 flex items-center">
                            <svg-icon name="logo" class="size-10 md:size-14 mb-4 mr-4" />
                            {{ data?.title }}
                        </h1>
                        <div class="font-lead" v-html="data?.ingress"></div>
                        <div class="bg-sand-30 text-fjell p-2 my-8 rounded-md">
                            <button
                                type="button"
                                class="p-4 pl-6 flex flex-col sm:flex-row items-center gap-4 justify-between w-full text-left"
                                @click="expand = !expand"
                            >
                                <figure class="inline-block flex-none size-[100px]">
                                    <Lottie
                                        src="/lottie/shrug.json"
                                        :loop="true"
                                        :width="100"
                                        :height="100"
                                    />
                                </figure>
                                <div>
                                    <div class="font-heading3 mb-2">
                                        {{ data?.infoboks.title }}
                                    </div>
                                    <p>
                                        {{ data?.infoboks.text }}
                                        <a :href="localePath('/manus')" class="underline">
                                            {{ data?.infoboks.link }} </a
                                        >.
                                    </p>
                                </div>
                                <span
                                    class="rounded-full border border-natt h-9 w-9 flex-none flex items-center"
                                    :class="expand ? 'rotate-180' : ''"
                                >
                                    <svg-icon name="chevron" class="w-5 h-5 mx-auto" />
                                </span>
                            </button>
                            <Collapse :when="expand">
                                <div class="p-6 pt-0">
                                    <ol class="list-decimal pl-4 space-y-6">
                                        <li v-for="text in data?.infoboks.list">
                                            {{ text }}
                                        </li>
                                    </ol>
                                </div>
                            </Collapse>
                        </div>
                        <p class="my-8" v-html="data?.content"></p>
                        <div class="flex flex-wrap justify-center gap-4 items-start mt-4">
                            <button
                                class="btn bg-nordlys text-white flex items-center border-2 border-nordlys"
                                @click="newRoom"
                            >
                                {{ loggedIn ? 'Start nytt rom' : data?.anonymousButton }}
                                <svg-icon name="chevron" class="size-5 -rotate-90 ml-2" />
                            </button>

                            <button
                                v-if="!loggedIn"
                                v-for="provider in providers"
                                :key="provider.name"
                                type="button"
                                class="btn bg-white border-vann border-2 text-vann flex items-center gap-2"
                                :disabled="provider.disabled"
                                @click="login(provider.name)"
                            >
                                <svg-icon name="feide" class="size-5" />
                                <span class="pl-2">
                                    {{ data?.feide.login }} {{ provider.label }}
                                </span>
                            </button>
                            <button
                                v-if="loggedIn"
                                class="btn bg-vann border-vann border-2 text-white flex items-center gap-2"
                                @click="logout(currentProvider)"
                            >
                                <svg-icon name="feide" class="size-5" />
                                <span class="pl-2">{{ data?.feide.logout }}</span>
                            </button>
                        </div>
                    </div>
                    <div class="bg-frost-30 p-6 md:p-16 rounded-b-xl">
                        <h2 class="font-heading3 flex items-center text-vann">
                            {{ data?.rooms.title }}
                        </h2>
                        <p class="text-vann">{{ data?.rooms.text }}</p>
                        <transition-group
                            name="list"
                            tag="ul"
                            class="mt-4 flex flex-col gap-2 relative"
                        >
                            <li
                                v-for="room in rooms"
                                :key="room.code"
                                class="w-full bg-white p-4 md:pr-8 flex flex-col md:flex-row md:items-center gap-4 md:gap-12 rounded transition-shadow hover:shadow-lg"
                            >
                                <nuxt-link
                                    :to="`/rom/${room.code}/admin`"
                                    class="grow group flex flex-col md:flex-row gap-4"
                                >
                                    <div class="w-[120px]">
                                        <span class="underline group-hover:no-underline">
                                            {{ room.code }}
                                        </span>
                                        <br />
                                        <time
                                            :datetime="$dayjs(room.createdAt).utc().toString()"
                                            class="w-[140px] font-small text-mørk-grå"
                                        >
                                            {{ $dayjs(room.createdAt).format('LL') }}
                                        </time>
                                        <br />
                                        <div v-if="room.showWarning" class="font-small">
                                            <span class="text-bær">
                                                {{ data?.rooms.soondelete }}
                                            </span>
                                        </div>
                                    </div>
                                    <div
                                        class="font-small text-mørk-grå flex flex-col md:flex-row md:items-center gap-4 grow"
                                    >
                                        <strong
                                            :class="[
                                                ferdig(room.step) ? 'text-nordlys' : 'text-fjell',
                                                'w-[55px]'
                                            ]"
                                            v-if="room.step === 0 || ferdig(room.step)"
                                        >
                                            {{
                                                ferdig(room.step)
                                                    ? t('Ferdig')
                                                    : room.step === 0 && t('Intro')
                                            }}
                                        </strong>
                                        <div
                                            v-if="!(room.step === 0 || ferdig(room.step))"
                                            class="flex items-center gap-2 text-fjell grow"
                                        >
                                            <strong class="w-[15px] text-right">
                                                {{ room.step }}
                                            </strong>
                                            <div
                                                class="min-w-[70px] grow rounded bg-vann-30 h-2 relative"
                                            >
                                                <span
                                                    :class="[
                                                        ferdig(room.step)
                                                            ? 'bg-nordlys'
                                                            : 'bg-fjell',
                                                        ' absolute left-0 inset-y-0 rounded'
                                                    ]"
                                                    :style="`width: ${(room.step / 16) * 100}%;`"
                                                ></span>
                                            </div>
                                            <strong>16</strong>
                                        </div>
                                    </div>
                                </nuxt-link>
                                <div class="inline-flex flex-col gap-3 font-small">
                                    <button
                                        @click="deleteRoom(room.code)"
                                        type="button"
                                        class="text-bær bg-bær-30 btn hover:bg-bær hover:text-white hover text-right"
                                    >
                                        {{ data?.rooms.delete }}
                                    </button>
                                    <button
                                        v-if="room.showWarning"
                                        @click="extendRoom(room.code)"
                                        type="button"
                                        class="underline hover:no-underline"
                                    >
                                        {{ data?.rooms.postpone }}
                                    </button>
                                </div>
                            </li>
                            <li
                                v-if="rooms?.length == 0"
                                :key="'none'"
                                class="bg-white p-4 pr-8 flex items-center gap-12 rounded"
                            >
                                {{ data?.rooms.norooms }}
                            </li>
                        </transition-group>
                    </div>
                </div>
                
                <div class="bg-frost-30 rounded-xl p-8">
                    <h4 class="mb-4">{{ data?.tip1Title }}</h4>
                    <p v-html="data?.tip1Content"></p>
                    <button
                        class="btn bg-white border-2 flex items-center mt-4"
                        @click="testSound()"
                    >
                        <svg-icon :name="isPlaying ? 'pause' : 'play'" class="w-5 h-5 mr-2" />
                        {{ data?.tip1Button }}
                    </button>
                    <audio class="opacity-0" ref="lydfil" crossorigin="anonymous">
                        <source src="https://assets.mustasj.dev/pengemester/media/lydtest.mp3" />
                    </audio>
                </div>
                <div class="bg-frost-30 rounded-xl p-8">
                    <h4 class="mb-4">{{ data?.tip2Title }}</h4>
                    <p v-html="data?.tip2Content"></p>
                    <button class="btn bg-white border-2 flex items-center mt-4" @click="toggle">
                        <svg-icon name="fullskjerm" class="w-5 h-5 mr-2" />
                        {{ isFullscreen ? t('Lukke fullskjerm') : t('Åpne fullskjerm') }}
                    </button>
                </div>
            </ContentRenderer>
        </div>
    </main>
</template>

<script setup lang="ts">
useSeoMeta({ robots: 'noindex, nofollow' });
import { Collapse } from 'vue-collapsed';
import { useFullscreen } from '@vueuse/core';
const { isFullscreen, toggle } = useFullscreen();
const { $dayjs } = useNuxtApp();
const { loggedIn, login, logout, currentProvider } = useOidcAuth();
const userStore = useUserStore();
const { providers } = useProviders(currentProvider.value as string);
const { locale, t } = useI18n();
const localePath = useLocalePath();
const { data } = await useAsyncData('data', () =>
    queryContent(`${locale.value}/nytt-rom`).findOne()
);
const ctx = useNuxtApp();
interface extendedRoom extends Room {
    showWarning: boolean;
}
const rooms = ref<extendedRoom[]>();
const expand = ref(false);
const utgaar = $dayjs().subtract(5.5, 'months');
let socket: any;
onMounted(() => {
    socket = ctx.$nuxtSocket({ path: '/ws/socket.io' });
    socket.emit('my-rooms', userStore.id);
    socket.on('my-rooms', (msg: extendedRoom[]) => {
        rooms.value = msg
            .map(room => {
                room.showWarning = $dayjs(room.updatedAt).isBefore(utgaar);
                return room;
            })
            .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    });
    socket.on('new-room', (msg: string) => {
        if (locale.value === 'nn') {
            navigateTo(`/nn/rom/${msg}/admin`);
        } else {
            navigateTo(`/rom/${msg}/admin`);
        }
    });
});
const ferdig = (steg: number) => {
    return steg === 16;
};
const newRoom = () => {
    useTrackEvent('Kurs', {
        props: { type: 'startet', loggedIn: loggedIn.value, locale: locale.value }
    });
    socket.emit('new-room', userStore.id);
};
const deleteRoom = (code: string) => {
    if (window.confirm(`${data.value?.delete} ${code}?`)) {
        socket.emit('delete-room', { code, uuid: userStore.id });
    }
};

const extendRoom = (code: string) => {
    socket.emit('extend-room', { code, uuid: userStore.id });
};
const isPlaying = ref(false);
const lydfil = ref<HTMLAudioElement>();
const testSound = () => {
    if (isPlaying.value) {
        lydfil.value?.pause();
    } else {
        lydfil.value?.play();
    }
    isPlaying.value = !lydfil.value?.paused;
};
</script>

<style scoped>
.list-move, /* apply transition to moving elements */
.list-enter-active,
.list-leave-active {
    transition: all 0.3s cubic-bezier(0.55, 0, 0.1, 1);
}

.list-enter-from,
.list-leave-to {
    opacity: 0;
    transform: scaleY(0.01) translate(20px, 0);
}

/* ensure leaving items are taken out of layout flow so that moving
   animations can be calculated correctly. */
.list-leave-active {
    position: absolute;
}
</style>
